import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const EMPTY_SELECTIONS = [
  { questionId: '', answer: '' },
  { questionId: '', answer: '' },
  { questionId: '', answer: '' },
];

export default function SecurityQuestionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const required = searchParams.get('mode') === 'required' || location.state?.required === true;
  const [questions, setQuestions] = useState([]);
  const [selections, setSelections] = useState(EMPTY_SELECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    api.securityQuestions.get()
      .then((response) => {
        if (!mounted) return;
        const data = response.data?.data || {};
        if (data.hasAnswers) {
          navigate('/', { replace: true });
          return;
        }
        setQuestions(Array.isArray(data.questions) ? data.questions : []);
      })
      .catch((err) => {
        if (mounted) setError(err.response?.data?.message || 'Unable to load security questions. Please try again.');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [navigate]);

  const selectedIds = useMemo(
    () => selections.map((item) => item.questionId).filter(Boolean),
    [selections]
  );

  const updateSelection = (index, field, value) => {
    setSelections((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
    if (error) setError('');
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');

    if (selections.some((item) => !item.questionId || !item.answer.trim())) {
      setError('Select and answer all 3 security questions.');
      return;
    }
    if (new Set(selectedIds).size !== 3) {
      setError('Choose 3 different security questions.');
      return;
    }

    setSaving(true);
    try {
      await api.securityQuestions.save(
        selections.map(({ questionId, answer }) => ({ questionId, answer }))
      );
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save your security questions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ maxWidth: 520, margin: '0 auto', padding: '70px 20px', textAlign: 'center', color: '#aaa' }}>Loading security questions…</div>;
  }

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '50px 20px 70px', color: '#fff' }}>
      <div style={{ textAlign: 'center', marginBottom: 30 }}>
        <div style={{ fontSize: 26, fontWeight: 900, marginBottom: 18 }}>9ja<span style={{ color: '#00d4aa' }}>Win</span></div>
        <h1 style={{ fontSize: '1.75rem', marginBottom: 8 }}>
          {required ? 'Set up your recovery questions' : 'Add recovery questions'}
        </h1>
        <p style={{ color: '#888', lineHeight: 1.6, margin: 0 }}>
          Choose 3 questions and give answers you will remember. Your answers are securely hashed and are never stored in plain text.
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,77,77,.1)', border: '1px solid #ff4d4d', color: '#ff4d4d', padding: '12px 16px', borderRadius: 10, marginBottom: 20, fontSize: 14 }}>
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSave}>
        {selections.map((selection, index) => (
          <div key={index} style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 7, color: '#aaa', fontSize: 13, fontWeight: 700 }}>
              QUESTION {index + 1}
            </label>
            <select
              value={selection.questionId}
              onChange={(event) => updateSelection(index, 'questionId', event.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '13px 14px', borderRadius: 10, border: '1px solid #2a2a2a', background: '#1a1a1a', color: '#fff', outline: 'none' }}
            >
              <option value="">Select a question</option>
              {questions.map((question) => (
                <option key={question.id} value={question.id} disabled={selectedIds.includes(question.id) && selection.questionId !== question.id}>
                  {question.text}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={selection.answer}
              onChange={(event) => updateSelection(index, 'answer', event.target.value)}
              maxLength={200}
              autoComplete="off"
              placeholder="Your answer"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '13px 14px', borderRadius: 10, border: '1px solid #2a2a2a', background: '#1a1a1a', color: '#fff', outline: 'none' }}
            />
          </div>
        ))}

        <button type="submit" disabled={saving} style={{ width: '100%', padding: 14, border: 0, borderRadius: 10, background: '#00d4aa', color: '#000', fontWeight: 800, fontSize: 16, opacity: saving ? .6 : 1 }}>
          {saving ? 'Saving securely…' : 'Save Security Questions'}
        </button>
      </form>

      {!required && (
        <button
          type="button"
          onClick={() => navigate('/', { replace: true })}
          style={{ display: 'block', margin: '18px auto 0', border: 0, background: 'transparent', color: '#888', fontWeight: 700, cursor: 'pointer' }}
        >
          Skip for now
        </button>
      )}

      <p style={{ color: '#555', fontSize: 12, lineHeight: 1.5, textAlign: 'center', marginTop: 24 }}>
        You can add or update these questions later. They are being collected now so they can support future account-recovery and new-device security features.
      </p>
    </div>
  );
}
